import { Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Icon from "../../lib/components/custom/Icon";
import { RestaurantClosedBanner } from "../../lib/components/custom/RestaurantClosedBanner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../lib/components/ui/alert-dialog";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { calculateExtrasTotal } from "../../lib/constants";
import {
	getNextOpenTime,
	isRestaurantOpen,
} from "../../lib/constants/restaurant-hours";
import { useApi } from "../../lib/hooks/api/use-api";
import ExtrasDialog from "../home/components/ExtrasDialog";
import ItemDialog from "../home/components/ItemDialog";
import Layout from "../layout";
import { CartItemCard } from "./CartItemCard";

// Animation variants
const fadeInUp = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, x: -100, transition: { duration: 0.2 } },
	transition: { duration: 0.3, ease: "easeOut" },
};

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.05,
		},
	},
};

export default function Cart() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { auth, cart } = useApi();

	const { data: session } = auth.useSession();
	const {
		data: cartData,
		isLoading,
		error,
		refetch,
	} = cart.getByUserId(session?.user?.id || "");
	const updateItemMutation = cart.updateItem;
	const removeItemMutation = cart.removeItem;

	const [editingItem, setEditingItem] = useState<any>(null);
	const [editMode, setEditMode] = useState<"size" | "extras" | null>(null);
	const [removingItem, setRemovingItem] = useState<any>(null);

	// Fetch full menu item details when editing
	const { data: fullMenuItemData } = useApi().menu.getItemById(
		editingItem?.menuItemId || 0,
	);

	const items = cartData?.cartItems || [];

	// Calculate grand total
	const total = items.reduce((sum: number, item) => {
		const basePrice = parseFloat(item.menuSize.price);
		const extrasPrice = calculateExtrasTotal(
			item.extras,
			item.menuSize.sizeName,
		);
		return sum + (basePrice + extrasPrice) * item.quantity;
	}, 0);

	const itemCount = items.reduce((sum: number, item) => sum + item.quantity, 0);

	// Restaurant hours check
	const restaurantOpen = isRestaurantOpen();
	const nextOpenTime = getNextOpenTime();

	const handleQuantityChange = async (
		cartItemId: number,
		newQuantity: number,
	) => {
		if (!session?.user?.id) {
			toast.error(t("cart.toast.sign_in_required"));
			return;
		}

		try {
			if (newQuantity < 1) {
				await removeItemMutation.mutateAsync(cartItemId);
				toast.success(t("cart.toast.item_removed"));
			} else {
				await updateItemMutation.mutateAsync({
					cartItemId,
					quantity: { quantity: newQuantity },
				});
				toast.success(t("cart.toast.cart_updated"));
			}
			refetch();
		} catch (error) {
			console.error("Error updating cart item:", error);
			toast.error(t("cart.toast.update_failed"));
		}
	};

	if (!session?.user?.id) {
		return (
			<Layout>
				<div className="p-4 md:p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-4xl mx-auto">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
						>
							<Card className="p-6 sm:p-8 lg:p-12 text-center">
								<div className="flex flex-col items-center gap-4">
									<Icon
										name="LogIn"
										className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground"
									/>
									<div>
										<h2 className="text-lg sm:text-xl font-semibold mb-2">
											{t("cart.sign_in_required.header")}
										</h2>
										<p className="text-sm sm:text-base text-muted-foreground mb-6">
											{t("cart.sign_in_required.subheader")}
										</p>
										<Button asChild className="w-full sm:w-auto">
											<Link to="/auth/signin">
												{t("cart.sign_in_required.button")}
											</Link>
										</Button>
									</div>
								</div>
							</Card>
						</motion.div>
					</div>
				</div>
			</Layout>
		);
	}

	if (isLoading) {
		return (
			<Layout>
				<div className="p-4 sm:p-6 lg:p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-4xl mx-auto">
						<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
							<div>
								<h1 className="text-2xl sm:text-3xl font-manrope font-semibold">
									{t("cart.header")}
								</h1>
								<Skeleton className="h-3 sm:h-4 w-40 sm:w-48 mt-1" />
							</div>
						</div>
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
							<div className="lg:col-span-2 space-y-3 sm:space-y-4">
								{Array.from({ length: 3 }).map((_, i) => (
									<Card key={i} className="p-4 sm:p-6">
										<Skeleton className="h-16 sm:h-20 w-full" />
									</Card>
								))}
							</div>
							<Card className="p-4 sm:p-6">
								<Skeleton className="h-28 sm:h-32 w-full" />
							</Card>
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	if (error) {
		return (
			<Layout>
				<div className="p-4 sm:p-6 lg:p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-4xl mx-auto">
						<Card className="p-6 sm:p-8 lg:p-12 text-center">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="CircleAlert"
									className="w-12 h-12 sm:w-16 sm:h-16 text-destructive"
								/>
								<div>
									<h2 className="text-lg sm:text-xl font-semibold mb-2">
										{t("cart.error.header")}
									</h2>
									<p className="text-sm sm:text-base text-muted-foreground mb-6">
										{t("cart.error.subheader")}
									</p>
									<Button
										onClick={() => refetch()}
										className="w-full sm:w-auto"
									>
										{t("cart.error.retry")}
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-6 lg:p-8 border rounded-large bg-gradient-main min-h-full">
				<div className="max-w-4xl md:max-w-6xl mx-auto">
					{/* Header */}
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-12">
						<div className="space-y-3">
							<div className="flex items-center gap-3 sm:gap-4">
								<div className="p-2 sm:p-3 bg-primary/10 rounded-xl">
									<Icon
										name="ShoppingCart"
										className="w-6 h-6 sm:w-7 sm:h-7 text-primary"
									/>
								</div>
								<div>
									<h1 className="text-2xl sm:text-4xl font-manrope font-bold text-foreground">
										{t("cart.header")}
									</h1>
									<p className="text-sm sm:text-lg text-muted-foreground mt-1">
										{itemCount === 0
											? t("cart.empty")
											: t(
													itemCount === 1
														? "cart.items_count"
														: "cart.items_count_plural",
													{ count: itemCount },
												)}
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Restaurant Closed Banner */}
					{!restaurantOpen && itemCount > 0 && (
						<div className="mb-6">
							<RestaurantClosedBanner nextOpenTime={nextOpenTime} />
						</div>
					)}

					{itemCount === 0 ? (
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.2 }}
						>
							<Card className="p-6 sm:p-8 lg:p-12 text-center border-2 border-dashed border-muted-foreground/25 bg-muted/20">
								<div className="max-w-md mx-auto space-y-6">
									<div className="relative">
										<div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mx-auto flex items-center justify-center">
											<Icon
												name="ShoppingCart"
												className="w-8 h-8 sm:w-10 sm:h-10 text-primary"
											/>
										</div>
										<div className="absolute -top-1 -right-1 w-10 h-10 bg-muted rotate-10 rounded-full flex items-center justify-center">
											<Icon
												name="IceCreamCone"
												className="w-5 sm:w-7 text-secondary"
											/>
										</div>
									</div>
									<div className="space-y-3">
										<h2 className="text-xl sm:text-2xl font-manrope font-semibold text-foreground">
											{t("cart.empty_state.header")}
										</h2>
										<p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
											{t("cart.empty_state.subheader")}
										</p>
									</div>
									<Button
										variant="primary"
										asChild
										className="w-full sm:w-auto"
									>
										<Link to="/">{t("cart.empty_state.browse_menu")}</Link>
									</Button>
								</div>
							</Card>
						</motion.div>
					) : (
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
							{/* Cart Items */}
							<div className="lg:col-span-2 space-y-3 sm:space-y-4">
								<motion.div
									variants={staggerContainer}
									initial="initial"
									animate="animate"
									className="space-y-4"
								>
									<AnimatePresence mode="popLayout">
										{items.map((item) => (
											<motion.div
												key={item.id}
												layout
												variants={fadeInUp}
												className="group"
											>
												<CartItemCard
													item={item}
													onEditSize={() => {
														setEditingItem(item);
														setEditMode("size");
													}}
													onEditExtras={() => {
														setEditingItem(item);
														setEditMode("extras");
													}}
													onRequestRemove={() => setRemovingItem(item)}
													onDecrease={() =>
														item.quantity > 1
															? handleQuantityChange(item.id, item.quantity - 1)
															: setRemovingItem(item)
													}
													onIncrease={() =>
														handleQuantityChange(item.id, item.quantity + 1)
													}
													isUpdating={
														updateItemMutation.isPending ||
														removeItemMutation.isPending
													}
												/>
											</motion.div>
										))}
									</AnimatePresence>
								</motion.div>
							</div>

							{/* Order Summary */}
							<div className="lg:col-span-1">
								<Card className="p-4 sm:p-6 lg:sticky lg:top-4 border-0 shadow-lg bg-white rounded-xl overflow-hidden">
									<div className="flex items-center gap-3 mb-4 sm:mb-6">
										<div className="p-2 bg-primary/10 rounded-lg">
											<Icon
												name="Receipt"
												className="w-4 h-4 sm:w-5 sm:h-5 text-primary"
											/>
										</div>
										<h2 className="text-lg sm:text-xl font-manrope font-bold text-foreground">
											{t("cart.summary.header")}
										</h2>
									</div>

									<div className="space-y-3">
										<div className="flex justify-between items-center py-2 border-b border-dashed">
											<span className="text-sm sm:text-base text-muted-foreground">
												{t("cart.summary.subtotal")}
											</span>
											<span className="text-sm sm:text-base font-medium">
												€{total.toFixed(2)}
											</span>
										</div>
									</div>

									<div className="bg-muted/30 py-3 px-1 rounded-xl mb-2">
										<div className="flex justify-between items-center">
											<span className="text-base sm:text-lg font-semibold text-foreground">
												{t("cart.summary.total")}
											</span>
											<span className="text-xl sm:text-xl font-bold text-primary">
												€{total.toFixed(2)}
											</span>
										</div>
									</div>

									<Button
										className="w-full h-11 sm:h-12 text-sm sm:text-base shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all"
										variant="primary"
										size="lg"
										onClick={() => navigate({ to: "/checkout" })}
									>
										{t("cart.summary.checkout")}
									</Button>
								</Card>
							</div>
						</div>
					)}
				</div>

				{/* Edit Dialogs */}
				{editingItem && (
					<>
						<ItemDialog
							open={editMode === "size"}
							onOpenChange={(open) => !open && setEditMode(null)}
							item={fullMenuItemData?.item || editingItem.menuItem}
							sizes={fullMenuItemData?.sizes || []}
							cartItem={editingItem}
						/>

						{/* We use conditional rendering for ExtrasDialog to ensure it gets fresh props */}
						{editMode === "extras" && (
							<ExtrasDialog
								open={true}
								onOpenChange={(open) => !open && setEditMode(null)}
								item={fullMenuItemData?.item || editingItem.menuItem}
								cartItem={editingItem}
								selectedSize={editingItem.menuSizeId?.toString() || ""}
								selectedSizeName={editingItem.menuSize?.sizeName || ""}
								quantity={editingItem.quantity}
								basePrice={parseFloat(editingItem.menuSize?.price || "0")}
							/>
						)}
					</>
				)}

				{/* Remove Item Confirmation Dialog */}
				<AlertDialog
					open={!!removingItem}
					onOpenChange={(open) => !open && setRemovingItem(null)}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t("cart.remove_dialog.title")}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t("cart.remove_dialog.description", {
									itemName: removingItem?.menuItem?.name || "",
								})}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t("cart.remove_dialog.cancel")}
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => {
									if (removingItem) {
										handleQuantityChange(removingItem.id, 0);
										setRemovingItem(null);
									}
								}}
								className="bg-primary text-primary-foreground hover:bg-primary/90"
							>
								{t("cart.remove_dialog.confirm")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</Layout>
	);
}
